/*
 * knobs-search-ga4.js  (added 2026-07-09, post-Typesense-cutover)
 * ─────────────────────────────────────────────────────────────────────────
 * Pushes the Typesense search surfaces into the existing GTM dataLayer
 * (container GTM-WD3KKCR) so GA4 finally sees search behavior. Three signals:
 *
 *   1. view_search_results  — {search_term, ts_results_count} — fills the GA4
 *      site-search gap (the custom InstantSearch SPA never emitted this).
 *   2. select_item          — GA4 ecommerce item-list click from search /
 *      autocomplete — this is the search→product→purchase attribution path
 *      that REPLACES the now-dead Algolia queryID insights (window.aa fires
 *      post-cutover with no valid Typesense queryID, so it attributes nothing).
 *   3. ts_fallback          — every Typesense→Algolia fail-open. Logged through
 *      GTM/GA4, which is INDEPENDENT of Typesense — so if the cluster is down,
 *      this beacon still lands (the whole point per the review).
 *
 * Everything is wrapped so a failure here can NEVER break search — the callers
 * invoke via `window.KnobsGA4 && KnobsGA4.x()` and every method self-guards.
 */
(function () {
  'use strict';
  window.dataLayer = window.dataLayer || [];

  function push(obj) {
    try { window.dataLayer.push(obj); } catch (e) {}
  }

  // knobs.co delivers GA4 through gtag (G-HC2RB775HT), NOT a firing GTM
  // container — so a raw dataLayer.push({event:…}) object reaches nothing.
  // gtag('event',…) is the live pipe to GA4, so we send there directly. The
  // dataLayer.push above is kept as an inert hook for debugging / a future GTM
  // setup. NOTE: if GTM triggers are ever added for these event names, drop the
  // gtag() calls here to avoid double-counting.
  function ga4(name, params) {
    try { if (typeof window.gtag === 'function') window.gtag('event', name, params || {}); } catch (e) {}
  }

  // ---- 1. view_search_results (debounced + deduped per settled query) ----
  var _lastTerm = null, _timer = null;
  function emitSearch(term, count, surface) {
    var p = {
      search_term: term,
      ts_results_count: (typeof count === 'number' ? count : null),
      ts_surface: surface || 'instantsearch'
    };
    push(Object.assign({ event: 'view_search_results' }, p));
    ga4('view_search_results', p);
  }
  // Called from the searchClient wrapper on every resolved TS response.
  // Extracts the primary query + hit count and emits once per settled term.
  function observeResults(mappedRequests, response) {
    try {
      var r = response && response.results && response.results[0];
      if (!r) return;
      var term = (r.query || '').trim();
      if (!term) return;                    // ignore browse / empty
      var count = (typeof r.nbHits === 'number') ? r.nbHits : null;
      if (term === _lastTerm) return;       // same settled term already queued
      if (_timer) clearTimeout(_timer);
      _timer = setTimeout(function () {
        _lastTerm = term;
        emitSearch(term, count, 'instantsearch');
      }, 700);                              // settle window: fire after typing stops
    } catch (e) {}
  }

  // ---- 2. select_item (search / autocomplete result click) ----
  function selectItem(o) {
    try {
      o = o || {};
      var listName = o.surface === 'autocomplete' ? 'Search Autocomplete' : 'Typesense Search';
      var items = [{
        item_id: o.objectID != null ? String(o.objectID) : undefined,
        item_name: o.title || undefined,
        index: o.position != null ? parseInt(o.position, 10) : undefined
      }];
      push({ ecommerce: null });            // clear the previous ecommerce object (GTM path)
      push({
        event: 'select_item',
        ts_query: o.query || null,
        ts_surface: o.surface || 'instantsearch',
        ecommerce: { item_list_name: listName, items: items }
      });
      // GA4 (gtag) ecommerce select_item — the live delivery path.
      ga4('select_item', {
        item_list_name: listName,
        items: items,
        ts_query: o.query || undefined,
        ts_surface: o.surface || 'instantsearch'
      });
    } catch (e) {}
  }

  // ---- 3. ts_fallback (Typesense → Algolia fail-open) ----
  function fallback(o) {
    try {
      o = o || {};
      var err = o.error;
      var p = {
        ts_surface: o.surface || 'instantsearch',
        ts_error: String((err && (err.message || err.name)) || err || 'unknown').slice(0, 200),
        ts_page: (location.pathname || '') + (location.search || '')
      };
      push(Object.assign({ event: 'ts_fallback' }, p));
      ga4('ts_fallback', p);
    } catch (e) {}
  }

  window.KnobsGA4 = {
    observeResults: observeResults,
    emitSearch: emitSearch,
    selectItem: selectItem,
    fallback: fallback
  };
})();
